import React, { useContext } from 'react'
import { useQuery } from '@apollo/client'
import dayjs from 'dayjs'
import { Table } from 'antd'
import type { TablePaginationConfig } from 'antd/es/table'
import { LEGAL_NOTICES_INDEX_QUERY } from 'graphql/LegalNoticesIndex'
import CentredLoader from 'components/CentredLoader'
import { LegalNoticeIndexTableType } from './types'
import { columns } from './config'
import { VariablesContext } from '../../contexts/variablesContext'
import LegalNoticesActionsMenu from '../LegalNoticesActionsMenu'
import styled from 'styled-components'

interface Props {
  providerKey: string
}

const LegalNoticesTable = ({ providerKey }: Props): JSX.Element => {
  const { variables, setVariables, apiParameters } =
    useContext(VariablesContext)

  const { data, loading } = useQuery(LEGAL_NOTICES_INDEX_QUERY, {
    variables: { provider: providerKey, ...apiParameters() },
    fetchPolicy: 'no-cache',
  })

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, any> = {} // eslint-disable-line @typescript-eslint/no-explicit-any
  ) => {
    const updatedVariables = {
      ...variables,
      pagination: pagination || variables.pagination,
      filters: { ...variables.filters, ...filters },
    }
    setVariables(updatedVariables)
  }

  if (loading || !data) {
    return (
      <>
        <ActionsWrapper>
          <LegalNoticesActionsMenu
            tableLoading={loading}
            providerKey={providerKey}
          />
        </ActionsWrapper>
        <CentredLoader />
      </>
    )
  }

  const formatDate = (date: string | null) =>
    date ? dayjs(date).format('DD/MM/YYYY') : null

  const legalNotices = data.legalNoticesIndexPage.legalNotices
  const total = data.legalNoticesIndexPage.pagination.total

  const dataSource: LegalNoticeIndexTableType[] = legalNotices.map(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (ln: any) => ({
      // eslint-disable-line @typescript-eslint/no-explicit-any
      key: ln.id,
      id: ln.id,
      provider: ln.provider,
      status: ln.status,
      publicationDate: formatDate(ln.publicationDate),
      createdAt: ln.createdAt
        ? dayjs(ln.createdAt).format('DD/MM/YYYY HH:mm')
        : null,
      reference: ln.reference,
      customerReference: ln.customerReference,
      departmentCode: ln.departmentCode,
      priceHt: ln.priceHt,
      priceTtc: ln.priceTtc,
      externalRef: ln.externalRef,
      externalId: ln.externalId,
      legalFormalityId: ln.legalFormalityId,
      societyName: ln.societyName,
      email: ln.email,
    })
  )

  return (
    <>
      <ActionsWrapper>
        <LegalNoticesActionsMenu
          tableLoading={loading}
          providerKey={providerKey}
        />
      </ActionsWrapper>
      <Table
        dataSource={dataSource}
        columns={columns(variables, setVariables, providerKey)}
        pagination={{ ...variables.pagination, total }}
        scroll={{ x: 1600 }}
        onChange={handleTableChange}
        bordered
        size="small"
      />
    </>
  )
}

export default LegalNoticesTable

const ActionsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 20px 0 8px 0;
`
